<template>
  <div class="decision-aid">
    <div class="container">
      <LinkStepBack v-if="canGoOneStepBack && device !== 'mobile'" class="link-go-step-back"/>

      <h2 class="step-question">
        <span
          v-if="currentQuestion && !isFinished"
          v-text="currentQuestion.long_name"
        ></span>
        <span
          v-else-if="isFinished"
          v-text="$t('global.word.results') + '(' + productAmount + ')'"
        ></span>
      </h2>
      <p
        class="step-description"
        v-if="currentQuestion && !isFinished"
        v-html="currentQuestion.description">
      </p>


      <transition-group
        tag="div"
        class="answers"
        :class="{finished: isFinished}"
        :name="animationDirection"
        :duration="{leave: 250, enter: 500}"
        :mode="animationDirection === 'next' ? 'in-out' : 'out-in'"
      >
        <component
          :is="block.hasOwnProperty('price') ? 'card-product' : 'card-choice'"
          v-for="(block, i) in availableBlocks"
          :key="'answer_' + currentQuestion.id + '' + i"
          :option="block"
          :device="device"
          @nextStep="nextStep(block)"
        ></component>
      </transition-group>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import CardChoice from '@/components/library/CardChoice';
import CardProduct from '@/components/library/CardProduct';
import LinkStepBack from '@/components/LinkStepBack';
import { scrollToTopOfElement } from '@/utils';

export default {
  name: 'DecisionAid',

  components: {
    LinkStepBack,
    CardChoice,
    CardProduct,
  },

  computed: {
    ...mapGetters('preference', [
      'currentQuestion',
      'isFinished',
      'animationDirection',
      'canGoOneStepBack',
    ]),
    ...mapState('products', {
      'products': 'all',
    }),
    ...mapGetters('products', [
      'productAmount',
    ]),
    ...mapGetters({
      device: 'getDevice',
    }),

    availableBlocks() {
      if (!this.isFinished && this.currentQuestion) {
        return this.sortedAnswers;
      }

      if (!this.isFinished) {
        return [];
      }

      if (this.products.length > 0) {
        return this.sortedProducts;
      }

      return [{
        noResult: true,
        delivery_time: null,
        environment_id: null,
        id: null,
        image_url: this.publicImage('themes/elektramat/no-results.png'),
        label: this.$t('DecisionAid.no_direct_results'),
        price: null,
        sku: null,
        sort: null,
        url: '#',
        url_text: this.$t('DecisionAid.no_results'),
      }];
    },
    sortedAnswers() {
      return [...this.currentQuestion.answers].sort((a, b) => a.sort - b.sort);
    },
    sortedProducts() {
      return [...this.products].sort((a, b) => {
        if (a.price === null) {
          return 1;
        }

        if (b.price === null) {
          return -1;
        }

        if (a.price === b.price) {
          return a.delivery_time - b.delivery_time;
        }

        return a.price - b.price;
      });
    },
  },

  methods: {
    async nextStep(option) {
      const el = document.querySelector(`.${process.env.VUE_APP_CSS_PREFIX || 'eda'}`);
      await scrollToTopOfElement(el);
      this.$store.dispatch('preference/nextStep', option.id);
    },
  },
};
</script>

<style lang="scss">
.decision-aid {
  padding: 15px 0;

  .link-go-step-back {
    margin-bottom: 1rem;
  }

  .step-question {
    margin-top: 0;
    margin-bottom: 15px;
    font-weight: var(--configurator-step-question-font-weight);
    color: var(--configurator-primary);
    font-size: var(--configurator-step-question-font-size);

    @media only screen and (max-width: 576px) {
      font-size: 22px;
    }
  }

  .step-description {
    width: 100%;
    margin-bottom: 20px;
    font-size: var(--configurator-step-description-font-size);
    font-weight: 400;
    line-height: 1.625;
  }

  .answers {
    display: grid;
    gap: 24px;
    grid-template-columns: repeat(auto-fill, minmax(min(300px, 100%), 1fr));

    &.finished {
      grid-template-columns: repeat(auto-fill, minmax(min(240px, 100%), 1fr));
    }

    .card-wrapper {
      display: flex;
      width: 100%;
      justify-content: center;

      @media only screen and (min-width: 900px) {
        justify-content: left;
      }

      &.products {
        margin: 0;
      }
    }
  }
}

.step.next-enter-active,
.next-enter-active .answer-card {
  transition: all 0.25s 0.25s ease-out;
}

.step.next-leave-active,
.next-leave-active .answer-card {
  transition: all 0.25s ease-in;
}

.step.next-enter,
.next-enter .answer-card {
  transform: translateX(50px);
  opacity: 0;
}

.step.next-leave-to,
.next-leave-to .answer-card {
  transform: translateX(-50px);
  opacity: 0;
}

.step.previous-enter-active,
.previous-enter-active .answer-card {
  transition: all 0.25s 0.25s ease-out;
}

.step.previous-leave-active,
.previous-leave-active .answer-card {
  transition: all 0.25s ease-in;
}

.step.previous-enter,
.previous-enter .answer-card {
  transform: translateX(-50px);
  opacity: 0;
}

.step.previous-leave-to,
.previous-leave-to .answer-card {
  transform: translateX(50px);
  opacity: 0;
}

.step.previous-leave-active,
.step.next-leave-active {
  position: absolute;
}
</style>
